import { useMeidenConnectApiUnderlying } from "@meiden-connect/auth-frontend-sdk";
import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import type { WebConfig } from "../env";
import { getWebConfig } from "../env";

export type ApiRequest = (path: string, init?: RequestInit) => Promise<Response>;

interface ApiRequestProviderProps {
  readonly children: ReactNode;
  readonly request: ApiRequest;
}

interface RuntimeApiRequestProviderProps {
  readonly children: ReactNode;
}

interface CreateApiRequestOptions {
  readonly fetchImpl?: typeof fetch | undefined;
  readonly getAccessToken?: (() => Promise<string>) | undefined;
}

const ApiRequestContext = createContext<ApiRequest | null>(null);

export function ApiRequestProvider({ children, request }: ApiRequestProviderProps): JSX.Element {
  return <ApiRequestContext.Provider value={request}>{children}</ApiRequestContext.Provider>;
}

export function useApiRequest(): ApiRequest {
  const request = useContext(ApiRequestContext);
  if (!request) {
    throw new Error("useApiRequest() は ApiRequestProvider 配下で呼び出してください。");
  }
  return request;
}

export function MockApiRequestProvider({ children }: RuntimeApiRequestProviderProps): JSX.Element {
  const config = getWebConfig();
  const request = useMemo(() => createApiRequest(config), [config]);
  return <ApiRequestProvider request={request}>{children}</ApiRequestProvider>;
}

export function AuthenticatedApiRequestProvider({
  children,
}: RuntimeApiRequestProviderProps): JSX.Element {
  const config = getWebConfig();
  const apiUnderlying = useMeidenConnectApiUnderlying();
  const getAccessToken = useCallback(() => apiUnderlying.getAccessToken(), [apiUnderlying]);
  const request = useMemo(
    () => createApiRequest(config, { getAccessToken }),
    [config, getAccessToken]
  );
  return <ApiRequestProvider request={request}>{children}</ApiRequestProvider>;
}

export function createApiRequest(
  config: Pick<WebConfig, "apiBaseUrl" | "authMode">,
  options: CreateApiRequestOptions = {}
): ApiRequest {
  const fetchImpl = options.fetchImpl ?? fetch;
  const baseUrl = config.apiBaseUrl.replace(/\/$/, "");

  return async (path, init) => {
    const headers = buildApiHeaders(init?.headers);
    if (config.authMode === "required") {
      if (!options.getAccessToken) {
        throw new Error("authMode=required では getAccessToken が必要です。");
      }
      const accessToken = await options.getAccessToken();
      headers.set("authorization", `Bearer ${accessToken}`);
    }

    // API 呼び出しは必ず同じ認証情報を伴うよう、credentials を include に固定する。
    return fetchImpl(`${baseUrl}${path}`, {
      ...init,
      credentials: "include",
      headers,
    });
  };
}

function buildApiHeaders(extra?: HeadersInit): Headers {
  const headers = new Headers(extra);
  if (!headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  return headers;
}
